import React, { useMemo, useCallback, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiFilter, FiTarget, FiX, FiInfo, FiChevronDown, FiBuilding, FiGlobe, FiCpu, FiUsers, FiTrendingUp, FiMapPin, FiHash, FiLayers, FiGrid, FiShield } = FiIcons

const QuickNavigation = () => {
  const { 
    currentLang, 
    activeRange, 
    setQuickNavRange, 
    totalCount,
    searchQuery,
    originalTotalCount,
    translations,
    bankData
  } = useApp()

  const [isCollapsed, setIsCollapsed] = useState(false)
  const [viewMode, setViewMode] = useState('category') // 'category' or 'codeRange'

  // 台灣銀行分類系統（按機構類型）
  const taiwanBankCategories = useMemo(() => [
    { 
      id: 'all', 
      label: { en: 'All Banks', zh: '全部銀行' },
      icon: FiGrid, // 改為網格圖標
      color: 'blue',
      description: { en: 'All financial institutions', zh: '所有金融機構' }
    },
    { 
      id: 'central', 
      label: { en: 'Central Bank', zh: '中央銀行' },
      icon: FiTarget,
      color: 'red',
      codes: ['001'],
      description: { en: 'Central Bank of Taiwan', zh: '中華民國中央銀行' }
    },
    { 
      id: 'public', 
      label: { en: 'Public Banks', zh: '公營銀行' },
      icon: FiShield, // 改為盾牌圖標，代表政府保障
      color: 'emerald',
      codes: ['004', '005', '006', '007', '008', '009', '017', '050'],
      description: { en: 'Government-owned banks', zh: '政府所有銀行' }
    },
    { 
      id: 'private', 
      label: { en: 'Private Banks', zh: '民營銀行' },
      icon: FiTrendingUp,
      color: 'purple',
      codes: ['011', '012', '013', '016', '048', '053', '054', '101', '102', '103', '108', '147', '803', '805', '806', '807', '808', '809', '812', '814', '815', '816', '822'],
      description: { en: 'Privately-owned commercial banks', zh: '民營商業銀行' }
    },
    { 
      id: 'foreign', 
      label: { en: 'Foreign Banks', zh: '外商銀行' },
      icon: FiGlobe,
      color: 'indigo',
      codes: ['015', '021', '039', '052', '072', '075', '076', '081', '082', '085', '097', '810'],
      description: { en: 'International banks in Taiwan', zh: '在台外商銀行' }
    },
    { 
      id: 'digital', 
      label: { en: 'Digital Banks', zh: '數位銀行' },
      icon: FiCpu,
      color: 'cyan',
      codes: ['823', '824', '825'],
      description: { en: 'Pure digital banking services', zh: '純網路銀行服務' }
    },
    { 
      id: 'credit-coop', 
      label: { en: 'Credit Cooperatives', zh: '信用合作社' },
      icon: FiUsers,
      color: 'orange',
      codeRange: '114-224',
      description: { en: 'Local community credit unions', zh: '地方信用合作社' }
    },
    { 
      id: 'farmers', 
      label: { en: 'Farmers\' Associations', zh: '農會信用部' },
      icon: FiMapPin,
      color: 'green',
      codeRange: '251-684',
      description: { en: 'Agricultural credit departments', zh: '農會金融機構' }
    },
    { 
      id: 'postal', 
      label: { en: 'Postal Service', zh: '郵政金融' },
      icon: FiTarget,
      color: 'yellow',
      codes: ['700'],
      description: { en: 'Chunghwa Post financial services', zh: '中華郵政金融服務' }
    },
    { 
      id: 'fishermen', 
      label: { en: 'Fishermen\'s Associations', zh: '漁會信用部' },
      icon: FiMapPin,
      color: 'teal',
      codeRange: '951-971',
      description: { en: 'Fishermen\'s credit departments', zh: '漁會金融機構' }
    }
  ], [])

  // 台灣銀行代碼範圍分類（按代碼區間）
  const taiwanCodeRanges = useMemo(() => [
    { 
      id: 'all', 
      label: { en: 'All Codes', zh: '全部代碼' },
      icon: FiHash,
      color: 'blue',
      description: { en: 'All bank codes', zh: '所有銀行代碼' }
    },
    { 
      id: '001-099', 
      label: { en: '001-099', zh: '001-099' },
      icon: FiTarget,
      color: 'red',
      range: [1, 99],
      description: { en: 'Central Bank & Major Banks', zh: '中央銀行及主要銀行' }
    },
    { 
      id: '100-199', 
      label: { en: '100-199', zh: '100-199' },
      icon: FiBuilding,
      color: 'purple',
      range: [100, 199],
      description: { en: 'Regional & Specialized Banks', zh: '區域及專業銀行' }
    },
    { 
      id: '200-299', 
      label: { en: '200-299', zh: '200-299' },
      icon: FiUsers,
      color: 'orange',
      range: [200, 299],
      description: { en: 'Credit Cooperatives & Associations', zh: '信用合作社及協會' }
    },
    { 
      id: '300-499', 
      label: { en: '300-499', zh: '300-499' },
      icon: FiMapPin,
      color: 'green',
      range: [300, 499],
      description: { en: 'Regional Farmers\' Associations', zh: '各地區農會信用部' }
    },
    { 
      id: '500-699', 
      label: { en: '500-699', zh: '500-699' },
      icon: FiMapPin,
      color: 'emerald',
      range: [500, 699],
      description: { en: 'Southern Farmers\' Associations', zh: '南部農會信用部' }
    },
    { 
      id: '700-799', 
      label: { en: '700-799', zh: '700-799' },
      icon: FiTarget,
      color: 'yellow',
      range: [700, 799],
      description: { en: 'Postal & Government Services', zh: '郵政及政府金融服務' }
    },
    { 
      id: '800-899', 
      label: { en: '800-899', zh: '800-899' },
      icon: FiTrendingUp,
      color: 'indigo',
      range: [800, 899],
      description: { en: 'New Generation Banks', zh: '新世代銀行' }
    },
    { 
      id: '900-999', 
      label: { en: '900-999', zh: '900-999' },
      icon: FiMapPin,
      color: 'teal',
      range: [900, 999],
      description: { en: 'Fishermen\'s Associations & Others', zh: '漁會信用部及其他' }
    }
  ], [])

  const currentCategories = viewMode === 'category' ? taiwanBankCategories : taiwanCodeRanges

  const handleCategoryClick = useCallback((category) => {
    setQuickNavRange(category, viewMode)
  }, [setQuickNavRange, viewMode])

  const handleClearCategory = useCallback(() => {
    setQuickNavRange({ id: 'all' }, viewMode)
  }, [setQuickNavRange, viewMode])

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev)
  }, [])

  const toggleViewMode = useCallback(() => {
    setViewMode(prev => {
      const newMode = prev === 'category' ? 'codeRange' : 'category'
      // 重置篩選
      setQuickNavRange({ id: 'all' }, newMode)
      return newMode
    })
  }, [setQuickNavRange])

  const isActive = useCallback((categoryId) => {
    return !searchQuery && activeRange === categoryId
  }, [activeRange, searchQuery])

  const showFilterStatus = activeRange !== 'all' && !searchQuery
  const currentCategory = currentCategories.find(c => c.id === activeRange)
  const currentLabel = currentCategory?.label[currentLang] || activeRange

  // 計算每個分類的銀行數量
  const getCategoryCount = useCallback((category) => {
    if (category.id === 'all') return originalTotalCount
    
    if (!bankData) return 0

    if (viewMode === 'category') {
      if (category.codes) {
        return bankData.filter(bank => category.codes.includes(bank.code)).length
      }
      if (category.codeRange) {
        const [start, end] = category.codeRange.split('-').map(num => parseInt(num, 10))
        return bankData.filter(bank => {
          const code = parseInt(bank.code, 10)
          return code >= start && code <= end
        }).length
      }
    } else {
      // codeRange mode
      if (category.range) {
        const [start, end] = category.range
        return bankData.filter(bank => {
          const code = parseInt(bank.code, 10)
          return code >= start && code <= end
        }).length
      }
    }
    
    return 0
  }, [originalTotalCount, bankData, viewMode])

  return (
    <motion.div 
      id="quick-navigation"
      className="space-y-4 sm:space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-800 dark:to-indigo-800 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiFilter} className="w-4 h-4 text-blue-600 dark:text-blue-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {translations.quickNavTitle[currentLang]}
              </h3>
              <motion.button
                onClick={toggleCollapse}
                whileHover={{ 
                  y: -1,
                  boxShadow: "0 4px 12px -2px rgba(107, 114, 128, 0.25)",
                  transition: { duration: 0.2 }
                }}
                whileTap={{ y: 0 }}
                className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                title={isCollapsed 
                  ? (currentLang === 'zh' ? '展開分類' : 'Expand categories')
                  : (currentLang === 'zh' ? '折疊分類' : 'Collapse categories')
                }
              >
                <motion.div
                  animate={{ rotate: isCollapsed ? 0 : 180 }}
                  transition={{ duration: 0.2 }}
                >
                  <SafeIcon 
                    icon={FiChevronDown} 
                    className="w-4 h-4 text-gray-600 dark:text-gray-300" 
                  />
                </motion.div>
              </motion.button>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
              {isCollapsed 
                ? (currentLang === 'zh' ? '已折疊 - 點擊展開瀏覽分類' : 'Collapsed - Click to expand categories')
                : (currentLang === 'zh' ? '依銀行類型或代碼範圍快速篩選' : 'Filter by bank categories or code ranges')
              }
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {/* View Mode Toggle */}
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1"
            >
              <button
                onClick={toggleViewMode}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  viewMode === 'category'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <SafeIcon icon={FiLayers} className="w-3 h-3" />
                <span className="hidden sm:inline">
                  {currentLang === 'zh' ? '類型' : 'Type'}
                </span>
              </button>
              <button
                onClick={toggleViewMode}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  viewMode === 'codeRange'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <SafeIcon icon={FiHash} className="w-3 h-3" />
                <span className="hidden sm:inline">
                  {currentLang === 'zh' ? '代碼' : 'Code'}
                </span>
              </button>
            </motion.div>
          )}

          {searchQuery && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 text-xs sm:text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-700"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span className="hidden sm:inline">
                {currentLang === 'zh' ? '搜尋中，分類篩選已停用' : 'Searching, category filters disabled'}
              </span>
              <span className="sm:hidden">
                {currentLang === 'zh' ? '搜尋中' : 'Searching'}
              </span>
            </motion.div>
          )}

          <AnimatePresence>
            {showFilterStatus && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 bg-gradient-to-r rounded-lg border ${
                  currentCategory?.color === 'emerald' ? 'from-emerald-100 to-teal-100 dark:from-emerald-800/30 dark:to-teal-800/30 border-emerald-200 dark:border-emerald-600' :
                  currentCategory?.color === 'purple' ? 'from-purple-100 to-pink-100 dark:from-purple-800/30 dark:to-pink-800/30 border-purple-200 dark:border-purple-600' :
                  currentCategory?.color === 'indigo' ? 'from-indigo-100 to-blue-100 dark:from-indigo-800/30 dark:to-blue-800/30 border-indigo-200 dark:border-indigo-600' :
                  currentCategory?.color === 'cyan' ? 'from-cyan-100 to-blue-100 dark:from-cyan-800/30 dark:to-blue-800/30 border-cyan-200 dark:border-cyan-600' :
                  currentCategory?.color === 'orange' ? 'from-orange-100 to-yellow-100 dark:from-orange-800/30 dark:to-yellow-800/30 border-orange-200 dark:border-orange-600' :
                  currentCategory?.color === 'green' ? 'from-green-100 to-emerald-100 dark:from-green-800/30 dark:to-emerald-800/30 border-green-200 dark:border-green-600' :
                  currentCategory?.color === 'red' ? 'from-red-100 to-pink-100 dark:from-red-800/30 dark:to-pink-800/30 border-red-200 dark:border-red-600' :
                  currentCategory?.color === 'yellow' ? 'from-yellow-100 to-orange-100 dark:from-yellow-800/30 dark:to-orange-800/30 border-yellow-200 dark:border-yellow-600' :
                  currentCategory?.color === 'teal' ? 'from-teal-100 to-cyan-100 dark:from-teal-800/30 dark:to-cyan-800/30 border-teal-200 dark:border-teal-600' :
                  'from-blue-100 to-indigo-100 dark:from-blue-800/30 dark:to-indigo-800/30 border-blue-200 dark:border-blue-600'
                }`}
              >
                <div className="flex items-center gap-1 sm:gap-2">
                  {currentCategory && (
                    <SafeIcon 
                      icon={currentCategory.icon} 
                      className={`w-3 h-3 sm:w-4 sm:h-4 ${
                        currentCategory.color === 'emerald' ? 'text-emerald-600 dark:text-emerald-300' :
                        currentCategory.color === 'purple' ? 'text-purple-600 dark:text-purple-300' :
                        currentCategory.color === 'indigo' ? 'text-indigo-600 dark:text-indigo-300' :
                        currentCategory.color === 'cyan' ? 'text-cyan-600 dark:text-cyan-300' :
                        currentCategory.color === 'orange' ? 'text-orange-600 dark:text-orange-300' :
                        currentCategory.color === 'green' ? 'text-green-600 dark:text-green-300' :
                        currentCategory.color === 'red' ? 'text-red-600 dark:text-red-300' :
                        currentCategory.color === 'yellow' ? 'text-yellow-600 dark:text-yellow-300' :
                        currentCategory.color === 'teal' ? 'text-teal-600 dark:text-teal-300' :
                        'text-blue-600 dark:text-blue-300'
                      }`} 
                    />
                  )}
                  <span className={`font-medium text-xs sm:text-sm ${
                    currentCategory?.color === 'emerald' ? 'text-emerald-800 dark:text-emerald-200' :
                    currentCategory?.color === 'purple' ? 'text-purple-800 dark:text-purple-200' :
                    currentCategory?.color === 'indigo' ? 'text-indigo-800 dark:text-indigo-200' :
                    currentCategory?.color === 'cyan' ? 'text-cyan-800 dark:text-cyan-200' :
                    currentCategory?.color === 'orange' ? 'text-orange-800 dark:text-orange-200' :
                    currentCategory?.color === 'green' ? 'text-green-800 dark:text-green-200' :
                    currentCategory?.color === 'red' ? 'text-red-800 dark:text-red-200' :
                    currentCategory?.color === 'yellow' ? 'text-yellow-800 dark:text-yellow-200' :
                    currentCategory?.color === 'teal' ? 'text-teal-800 dark:text-teal-200' :
                    'text-blue-800 dark:text-blue-200'
                  }`}>
                    {currentLabel}
                  </span>
                  <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-semibold ${
                    currentCategory?.color === 'emerald' ? 'bg-emerald-200 dark:bg-emerald-700 text-emerald-800 dark:text-emerald-200' :
                    currentCategory?.color === 'purple' ? 'bg-purple-200 dark:bg-purple-700 text-purple-800 dark:text-purple-200' :
                    currentCategory?.color === 'indigo' ? 'bg-indigo-200 dark:bg-indigo-700 text-indigo-800 dark:text-indigo-200' :
                    currentCategory?.color === 'cyan' ? 'bg-cyan-200 dark:bg-cyan-700 text-cyan-800 dark:text-cyan-200' :
                    currentCategory?.color === 'orange' ? 'bg-orange-200 dark:bg-orange-700 text-orange-800 dark:text-orange-200' :
                    currentCategory?.color === 'green' ? 'bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-200' :
                    currentCategory?.color === 'red' ? 'bg-red-200 dark:bg-red-700 text-red-800 dark:text-red-200' :
                    currentCategory?.color === 'yellow' ? 'bg-yellow-200 dark:bg-yellow-700 text-yellow-800 dark:text-yellow-200' :
                    currentCategory?.color === 'teal' ? 'bg-teal-200 dark:bg-teal-700 text-teal-800 dark:text-teal-200' :
                    'bg-blue-200 dark:bg-blue-700 text-blue-800 dark:text-blue-200'
                  }`}>
                    {totalCount}
                  </span>
                </div>
                <button
                  onClick={handleClearCategory}
                  className={`p-1 rounded-full transition-colors ${
                    currentCategory?.color === 'emerald' ? 'text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-700' :
                    currentCategory?.color === 'purple' ? 'text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-700' :
                    currentCategory?.color === 'indigo' ? 'text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 hover:bg-indigo-200 dark:hover:bg-indigo-700' :
                    currentCategory?.color === 'cyan' ? 'text-cyan-600 hover:text-cyan-800 dark:text-cyan-400 dark:hover:text-cyan-200 hover:bg-cyan-200 dark:hover:bg-cyan-700' :
                    currentCategory?.color === 'orange' ? 'text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-200 hover:bg-orange-200 dark:hover:bg-orange-700' :
                    currentCategory?.color === 'green' ? 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200 hover:bg-green-200 dark:hover:bg-green-700' :
                    currentCategory?.color === 'red' ? 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 hover:bg-red-200 dark:hover:bg-red-700' :
                    currentCategory?.color === 'yellow' ? 'text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-700' :
                    currentCategory?.color === 'teal' ? 'text-teal-600 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-200 hover:bg-teal-200 dark:hover:bg-teal-700' :
                    'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-700'
                  }`}
                  title={currentLang === 'zh' ? '清除篩選' : 'Clear filter'}
                >
                  <SafeIcon icon={FiX} className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Category Buttons */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            {/* Mode Indicator */}
            <div className="mb-4 text-center">
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-400">
                <SafeIcon icon={viewMode === 'category' ? FiLayers : FiHash} className="w-3 h-3" />
                {viewMode === 'category' 
                  ? (currentLang === 'zh' ? '按機構類型分類' : 'By Institution Type')
                  : (currentLang === 'zh' ? '按代碼範圍分類' : 'By Code Range')
                }
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {currentCategories.map((category, index) => {
                const active = isActive(category.id)
                const disabled = !!searchQuery
                const count = getCategoryCount(category)
                
                return (
                  <motion.button
                    key={`${viewMode}-${category.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onClick={() => !disabled && handleCategoryClick(category)}
                    disabled={disabled}
                    whileHover={!disabled ? { 
                      y: -2,
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      transition: { duration: 0.2 }
                    } : {}}
                    whileTap={!disabled ? { y: 0 } : {}}
                    className={`relative p-4 sm:p-5 rounded-xl text-left transition-all duration-300 shadow-sm hover:shadow-lg group ${
                      disabled
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed opacity-60'
                        : active
                          ? `bg-gradient-to-br shadow-lg border-2 ${
                              category.color === 'emerald' ? 'from-emerald-500 to-teal-600 text-white border-emerald-400' :
                              category.color === 'purple' ? 'from-purple-500 to-pink-600 text-white border-purple-400' :
                              category.color === 'indigo' ? 'from-indigo-500 to-blue-600 text-white border-indigo-400' :
                              category.color === 'cyan' ? 'from-cyan-500 to-blue-600 text-white border-cyan-400' :
                              category.color === 'orange' ? 'from-orange-500 to-yellow-600 text-white border-orange-400' :
                              category.color === 'green' ? 'from-green-500 to-emerald-600 text-white border-green-400' :
                              category.color === 'red' ? 'from-red-500 to-pink-600 text-white border-red-400' :
                              category.color === 'yellow' ? 'from-yellow-500 to-orange-600 text-white border-yellow-400' :
                              category.color === 'teal' ? 'from-teal-500 to-cyan-600 text-white border-teal-400' :
                              'from-blue-500 to-indigo-600 text-white border-blue-400'
                            }`
                          : `bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:border-${category.color}-300 dark:hover:border-${category.color}-500 hover:bg-${category.color}-50 dark:hover:bg-${category.color}-900/20`
                    }`}
                  >
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${
                      active 
                        ? 'bg-white/20 backdrop-blur-sm'
                        : `bg-${category.color}-100 dark:bg-${category.color}-800/30 group-hover:bg-${category.color}-200 dark:group-hover:bg-${category.color}-700/50`
                    }`}>
                      <SafeIcon 
                        icon={category.icon} 
                        className={`w-5 h-5 ${
                          active 
                            ? 'text-white'
                            : `text-${category.color}-600 dark:text-${category.color}-400 group-hover:text-${category.color}-700 dark:group-hover:text-${category.color}-300`
                        }`} 
                      />
                    </div>

                    {/* Content */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className={`font-semibold text-sm sm:text-base ${
                          active 
                            ? 'text-white'
                            : `text-gray-900 dark:text-white group-hover:text-${category.color}-800 dark:group-hover:text-${category.color}-200`
                        }`}>
                          {category.label[currentLang]}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          active
                            ? 'bg-white/20 text-white'
                            : `bg-${category.color}-100 dark:bg-${category.color}-800/30 text-${category.color}-700 dark:text-${category.color}-300`
                        }`}>
                          {count}
                        </span>
                      </div>
                      <p className={`text-xs sm:text-sm leading-relaxed ${
                        active 
                          ? 'text-white/80'
                          : `text-gray-500 dark:text-gray-400 group-hover:text-${category.color}-600 dark:group-hover:text-${category.color}-300`
                      }`}>
                        {category.description[currentLang]}
                      </p>
                    </div>

                    {/* Active indicator - 移除 scale 動畫 */}
                    {active && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="absolute top-3 right-3 w-3 h-3 bg-white rounded-full shadow-sm"
                      />
                    )}
                  </motion.button>
                )
              })}
            </div>

            {/* Statistics */}
            <div className="flex items-center justify-center mt-6 sm:mt-8">
              {!searchQuery && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center text-xs sm:text-sm text-gray-500 dark:text-gray-400 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700"
                >
                  {activeRange === 'all' 
                    ? (currentLang === 'zh' 
                        ? `台灣共有 ${originalTotalCount} 家金融機構` 
                        : `${originalTotalCount} financial institutions in Taiwan`
                      )
                    : (currentLang === 'zh'
                        ? `${currentLabel}：${totalCount} 家機構`
                        : `${currentLabel}: ${totalCount} institutions`
                      )
                  }
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed State */}
      <AnimatePresence>
        {isCollapsed && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleCollapse}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800/50 dark:to-blue-800/30 rounded-xl border border-gray-200 dark:border-gray-700 hover:from-gray-100 hover:to-blue-100 dark:hover:from-gray-800 dark:hover:to-blue-800/50 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ 
                  y: -1,
                  rotate: 5,
                  boxShadow: "0 4px 12px -2px rgba(59, 130, 246, 0.25)",
                  transition: { duration: 0.2 }
                }}
                transition={{ duration: 0.2 }}
              >
                <SafeIcon icon={FiChevronDown} className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
              </motion.div>
              <div className="text-left">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                  {currentLang === 'zh' ? '台灣銀行分類導航' : 'Taiwan Bank Categories'}
                </span>
                <p className="text-xs text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {currentLang === 'zh' ? '點擊展開瀏覽分類或代碼範圍' : 'Click to explore categories or code ranges'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!searchQuery && activeRange !== 'all' && currentCategory && (
                <div className="flex items-center gap-2">
                  <SafeIcon 
                    icon={currentCategory.icon} 
                    className={`w-4 h-4 text-${currentCategory.color}-500`} 
                  />
                  <span className={`px-3 py-1 bg-${currentCategory.color}-100 dark:bg-${currentCategory.color}-800 text-${currentCategory.color}-800 dark:text-${currentCategory.color}-200 rounded-full text-xs font-medium`}>
                    {currentLabel}: {totalCount}
                  </span>
                </div>
              )}
              <motion.div
                animate={{ x: [0, 3, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
                className="text-blue-500 opacity-60"
              >
                <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
              </motion.div>
            </div>
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default QuickNavigation